import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {
  PROFESSIONAL_TYPE_LABELS,
  PROFESSIONAL_CATEGORIES,
  type ProfessionalCategoryId,
} from '../types/professionalRates';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { Card, Button } from '../components';
import {
  FileText,
  FileCheck,
  FolderOpen,
  Archive,
  Search,
  Briefcase,
  Star,
  ArrowRight,
  ChevronDown,
  RotateCcw,
  X,
  UserPlus,
  Download,
} from 'lucide-react';
import { EmployerCockpitSidebar } from '../components/EmployerCockpitSidebar';
import { EmployerCockpitHeader } from '../components/EmployerCockpitHeader';
import { ZijMenuMobielKnop } from '../components/ZijMenu';
import { B2bReviewsPanel } from '../components/B2bReviewsPanel';
import api from '../services/api';
import { getDossierDetail } from '../services/employerTrajectenApi';
import { toast } from 'react-hot-toast';

// Reassign modal - hertoewijzen aan andere professional
const ReassignModal: React.FC<{
  dossier: any;
  professionals: any[];
  preselectedProfessionalId?: string | null;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}> = ({ dossier, professionals, preselectedProfessionalId, onSubmit, onCancel, isSubmitting }) => {
  const { t } = useTranslation('employer');
  const [professionalId, setProfessionalId] = useState(preselectedProfessionalId || '');
  // Besluit 1.1: de lijst-API levert geen cliëntnaam meer; die komt uit het dossier-detail.
  const { data: detail } = useQuery({
    queryKey: ['employer-dossier-detail', dossier.id],
    queryFn: () => getDossierDetail(dossier.id),
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!professionalId || !detail) return;
    onSubmit({
      professional_id: professionalId,
      client_name: detail.client_name,
      client_address: detail.client_address || undefined,
      client_email: detail.client_email || undefined,
      client_phone: detail.client_phone || undefined,
      description: detail.description || undefined
    });
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{t('cockpit.reassignTitle')}</h3>
            <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            {t('cockpit.reassignDesc')}
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('cockpit.professional')} *</label>
              <select
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                value={professionalId}
                onChange={(e) => setProfessionalId(e.target.value)}
              >
                <option value="">{t('cockpit.selectProfessional')}</option>
                {professionals.map((p: any) => (
                  <option key={p.professional_id} value={p.professional_id}>
                    {p.first_name} {p.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('cockpit.clientName')}</label>
              <input
                type="text"
                readOnly
                className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg"
                value={detail?.client_name || ''}
              />
            </div>
            <div className="flex gap-3 justify-end pt-4">
              <Button type="button" variant="outline" onClick={onCancel}>
                {t('common:common.cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting || !professionalId || !detail}>
                {isSubmitting ? t('cockpit.creating') : t('cockpit.createDossier')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

/** Cockpit-views zitten niet meer in het linkermenu (Dashboard-reset); oude deeplinks met ?view= werken nog. */
type CockpitView =
  | 'trajecten'
  | 'reviews'
  | 'afgesloten_dossiers'
  | 'gespreksverslagen'
  | 'geplaatste_opdrachten'
  | 'jaarverslagen';

const VALID_COCKPIT_VIEWS: CockpitView[] = [
  'trajecten', 'reviews', 'afgesloten_dossiers', 'gespreksverslagen', 'geplaatste_opdrachten', 'jaarverslagen'
];

export const EmployerCockpitPage: React.FC = () => {
  const { t, i18n } = useTranslation('employer');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: _user } = useAuthStore();
  const queryClient = useQueryClient();
  const viewFromUrl = searchParams.get('view');
  // Oude deeplinks: gespreksverslagen staan (zonder medewerkernamen) onder het hoofdmenu Trajecten
  useEffect(() => {
    if (viewFromUrl === 'gespreksverslagen') navigate('/employer/trajecten/gespreksverslagen', { replace: true });
  }, [viewFromUrl, navigate]);
  const initialView = viewFromUrl && VALID_COCKPIT_VIEWS.includes(viewFromUrl as CockpitView)
    ? (viewFromUrl as CockpitView) : 'trajecten';
  const [activeView, setActiveView] = useState<CockpitView>(initialView);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const changeView = useCallback((view: CockpitView) => {
    setActiveView(view);
    navigate(`/employer/cockpit?view=${view}`, { replace: true });
  }, [navigate]);

  const viewTitles: Record<CockpitView, { title: string; subtitle?: string }> = {
    trajecten: { title: t('cockpit.trajectsStay'), subtitle: t('cockpit.sortByAcceptance') },
    reviews: { title: t('cockpit.reviews', 'Beoordelingen'), subtitle: t('cockpit.reviewsDesc', 'Anonieme beoordelingen van medewerkers over professionals.') },
    afgesloten_dossiers: { title: t('cockpit.closedDossiers', 'Afgesloten dossiers') },
    gespreksverslagen: { title: t('cockpit.gespreksverslagen', 'Gespreksverslagen') },
    geplaatste_opdrachten: { title: t('cockpit.geplaatsteOpdrachten', 'Geplaatste opdrachten'), subtitle: t('cockpit.placedAssignmentsDesc') },
    jaarverslagen: { title: t('cockpit.jaarverslagen', 'Jaarverslagen') },
  };

  useEffect(() => {
    const view = searchParams.get('view');
    if (view && VALID_COCKPIT_VIEWS.includes(view as CockpitView)) {
      setActiveView(view as CockpitView);
    }
  }, [searchParams]);
  const [reassignDossier, setReassignDossier] = useState<any | null>(null);
  const [reassignPreselectedProfessionalId, setReassignPreselectedProfessionalId] = useState<string | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [nameFilter, setNameFilter] = useState<string>('');
  const [jaarFilter, setJaarFilter] = useState<string>('');
  const [reportRoleFilter, setReportRoleFilter] = useState<string>('');
  const [gesprekProfessionalFilter, setGesprekProfessionalFilter] = useState<string>('');
  const [gesprekMedewerkerFilter, setGesprekMedewerkerFilter] = useState<string>('');
  const [gesprekIncludeClosed, setGesprekIncludeClosed] = useState<boolean>(false);
  const [reviewModalProfessional, setReviewModalProfessional] = useState<{
    professional_id: string;
    professional_name: string;
    average_rating: number;
    review_count: number;
    questions: Array<{ label: string; question: string; avg: number }>;
  } | null>(null);

  // Status mapping for "Nieuwe medewerker" column
  const getNieuweMedewerkerLabel = useCallback((status: string): string => {
    const map: Record<string, string> = {
      pending_acceptance: t('status.pending_acceptance'),
      afgewezen: t('status.afgewezen'),
      lopend: t('status.geaccepteerd'),
      active: t('status.geaccepteerd'),
      nieuwe_aanmelding: t('status.geaccepteerd'),
      intake_ingepland: t('status.geaccepteerd'),
      eerste_gesprek_ingepland: t('status.geaccepteerd'),
      tweede_gesprek_ingepland: t('status.geaccepteerd'),
      eindgesprek: t('status.geaccepteerd'),
      afsluiten: t('status.geaccepteerd'),
      archived: t('status.geaccepteerd'),
      on_hold: t('status.geaccepteerd'),
      closed: t('status.geaccepteerd')
    };
    return map[status] || status;
  }, [t]);

  // Status traject (zoals professional cockpit)
  const getStatusTrajectLabel = useCallback((status: string): string => {
    const map: Record<string, string> = {
      pending_acceptance: t('status.pending_acceptance'),
      afgewezen: t('status.afgewezen'),
      nieuwe_aanmelding: t('status.nieuwe_aanmelding'),
      intake_ingepland: t('status.intake_ingepland'),
      eerste_gesprek_ingepland: t('status.eerste_gesprek_ingepland'),
      tweede_gesprek_ingepland: t('status.tweede_gesprek_ingepland'),
      lopend: t('status.lopend'),
      eindgesprek: t('status.eindgesprek'),
      afsluiten: t('status.afsluiten'),
      archived: t('status.archived'),
      on_hold: t('status.on_hold'),
      closed: t('status.closed')
    };
    return map[status] || status;
  }, [t]);

  // Status labels for vacancy applications (geplaatste opdrachten)
  const getApplicationStatusLabel = (status: string): string => {
    const map: Record<string, string> = {
      nieuw: t('applicationStatus.nieuw'),
      geaccepteerd: t('applicationStatus.geaccepteerd'),
      onderhandelingen: t('applicationStatus.onderhandelingen'),
      afgerond: t('applicationStatus.afgerond'),
      afgewezen: t('applicationStatus.afgewezen'),
      uitgenodigd: t('applicationStatus.uitgenodigd')
    };
    return map[status] || status;
  };

  // Dossiers (trajecten - excl. archived/closed)
  const { data: dossiersRaw, isLoading: dossiersLoading } = useQuery({
    queryKey: ['employer-dossiers-cockpit'],
    queryFn: async () => {
      const response = await api.get('/employer/dossiers');
      return response.data.data || [];
    }
  });

  // Afgesloten dossiers
  const { data: closedDossiersRaw, isLoading: closedDossiersLoading } = useQuery({
    queryKey: ['employer-dossiers-closed'],
    queryFn: async () => {
      const response = await api.get('/employer/dossiers', { params: { status: 'closed' } });
      return response.data.data || [];
    },
    enabled: activeView === 'afgesloten_dossiers'
  });

  // Connections (actieve professionals)
  const { data: connectionsRaw } = useQuery({
    queryKey: ['employer-connections-cockpit'],
    queryFn: async () => {
      const response = await api.get('/employer/connections');
      return response.data.data || [];
    }
  });

  // Reviews per professional (voor score + modal)
  const { data: reviewsData } = useQuery({
    queryKey: ['employer-reviews-cockpit'],
    queryFn: async () => {
      const response = await api.get('/employer/reviews');
      return response.data.data || [];
    }
  });

  // Individuele reviews (anoniem, op datum) voor cockpit Reviews-view
  const { data: individualReviewsRaw, isLoading: individualReviewsLoading } = useQuery({
    queryKey: ['employer-reviews-individual'],
    queryFn: async () => {
      const response = await api.get('/employer/reviews/individual');
      return response.data.data || [];
    },
    enabled: activeView === 'reviews'
  });

  // Geplaatste opdrachten (vacatures + reacties) - voor monitoring
  const { data: geplaatsteOpdrachtenRaw, isLoading: geplaatsteOpdrachtenLoading } = useQuery({
    queryKey: ['employer-geplaatste-opdrachten'],
    queryFn: async () => {
      const response = await api.get('/employer/geplaatste-opdrachten');
      return response.data.data || [];
    },
    enabled: activeView === 'geplaatste_opdrachten'
  });

  // Jaarverslagen - altijd ophalen (blijven zichtbaar, niet gekoppeld aan verlopen trajecten)
  const { data: jaarverslagenRaw, isLoading: jaarverslagenLoading } = useQuery({
    queryKey: ['employer-reports', jaarFilter, reportRoleFilter],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (jaarFilter) params.jaar = jaarFilter;
      if (reportRoleFilter) params.professional_type = reportRoleFilter;
      const response = await api.get('/employer/reports', { params });
      return response.data.data || [];
    },
    enabled: activeView === 'jaarverslagen'
  });

  // Gespreksverslagen - overzicht alle verslagen
  const { data: gespreksverslagenRaw, isLoading: gespreksverslagenLoading } = useQuery({
    queryKey: ['employer-gespreksverslagen', gesprekProfessionalFilter, gesprekMedewerkerFilter, gesprekIncludeClosed],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (gesprekProfessionalFilter) params.professional_id = gesprekProfessionalFilter;
      if (gesprekMedewerkerFilter) params.medewerker = gesprekMedewerkerFilter;
      if (gesprekIncludeClosed) params.include_closed = 'true';
      const response = await api.get('/employer/gespreksverslagen', { params });
      return response.data.data || [];
    },
    enabled: activeView === 'gespreksverslagen'
  });

  const reviewsByProfessional = useMemo(() => {
    const map: Record<string, { average_rating: number; review_count: number; reviews_visible: boolean; reviews_needed: number; questions?: any[] }> = {};
    for (const r of reviewsData || []) {
      map[r.professional_id] = {
        average_rating: r.average_rating ?? 0,
        review_count: r.review_count ?? 0,
        // AVG-drempel van 3: pas vanaf drie evaluaties scores tonen
        reviews_visible: r.reviews_visible !== false,
        reviews_needed: r.reviews_needed ?? 0,
        questions: r.questions
      };
    }
    return map;
  }, [reviewsData]);

  const createDossierMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/employer/dossiers', data);
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('cockpit.dossierCreated'));
      queryClient.invalidateQueries({ queryKey: ['employer-dossiers-cockpit'] });
      setReassignDossier(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('cockpit.dossierCreateError'));
    }
  });

  const reactivateDossierMutation = useMutation({
    mutationFn: async (dossierId: string) => {
      const response = await api.patch(`/employer/dossiers/${dossierId}`, { reactivate: true });
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('cockpit.dossierReopened'));
      queryClient.invalidateQueries({ queryKey: ['employer-dossiers-cockpit'] });
      queryClient.invalidateQueries({ queryKey: ['employer-dossiers-closed'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('cockpit.dossierReopenError'));
    }
  });

  // Trajecten = actieve dossiers (samenwerkingen staan bij /employer/connections)
  const trajecten = useMemo(() => {
    const dossiers = dossiersRaw || [];
    return dossiers.filter((d: any) => !['archived', 'closed'].includes(d.status));
  }, [dossiersRaw]);

  const closedDossiers = useMemo(() => {
    const raw = closedDossiersRaw || [];
    // Besluit 1.1: sorteren op registratienummer, nooit op naam
    return [...raw].sort((a: any, b: any) => (a.dossier_code || '').localeCompare(b.dossier_code || ''));
  }, [closedDossiersRaw]);

  const activeConnections = useMemo(
    () => (connectionsRaw || []).filter((c: any) => c.status === 'active'),
    [connectionsRaw]
  );

  const tableRows = useMemo(() => {
    const dossierRows = trajecten.map((d: any) => {
      const rev = reviewsByProfessional[d.professional_id];
      return {
        id: d.id,
        dossier_id: d.id,
        registratienummer: d.dossier_code,
        professional_role: d.professional_role || '-',
        professional_name: `${d.professional_first_name || ''} ${d.professional_last_name || ''}`.trim(),
        professional_id: d.professional_id,
        average_rating: rev?.average_rating ?? null,
        review_count: rev?.review_count ?? 0,
        reviews_visible: rev?.reviews_visible !== false,
        reviews_needed: rev?.reviews_needed ?? 0,
        review_questions: rev?.questions,
        nieuwe_medewerker: getNieuweMedewerkerLabel(d.status),
        status_traject: getStatusTrajectLabel(d.status),
        professional_accepted_at: d.professional_accepted_at,
        end_date: d.end_date,
        acceptance_notes: d.acceptance_notes,
        isAfgewezen: d.status === 'afgewezen',
        isConnection: false,
        raw: d
      };
    });
    // Alleen trajecten (dossiers) tonen - samenwerkingen staan bij /employer/connections
    return dossierRows.sort((a: any, b: any) => {
      const dateA = a.professional_accepted_at ? new Date(a.professional_accepted_at).getTime() : 0;
      const dateB = b.professional_accepted_at ? new Date(b.professional_accepted_at).getTime() : 0;
      return dateB - dateA;
    });
  }, [trajecten, reviewsByProfessional, getNieuweMedewerkerLabel, getStatusTrajectLabel]);


  const professionalNameOptions = useMemo(() => {
    const seen = new Set<string>();
    const names: Array<{ id: string; label: string }> = [];
    for (const row of tableRows) {
      const name = (row.professional_name || '').trim();
      if (name && !seen.has(row.professional_id)) {
        seen.add(row.professional_id);
        names.push({ id: row.professional_id, label: name });
      }
    }
    return names.sort((a, b) => a.label.localeCompare(b.label));
  }, [tableRows]);

  const filteredTableRows = useMemo(() => {
    let rows = tableRows;
    if (roleFilter) {
      rows = rows.filter((row: any) => {
        const conn = connectionsRaw?.find((c: any) => c.professional_id === row.professional_id);
        if (conn?.professional_types) {
          return conn.professional_types.includes(roleFilter);
        }
        const dossier = (dossiersRaw || []).find((d: any) => d.professional_id === row.professional_id);
        if (dossier?.professional_types_from_screening) {
          const types = typeof dossier.professional_types_from_screening === 'string'
            ? JSON.parse(dossier.professional_types_from_screening || '[]')
            : dossier.professional_types_from_screening;
          return Array.isArray(types) && types.includes(roleFilter);
        }
        return false;
      });
    }
    if (nameFilter) {
      rows = rows.filter((row: any) => row.professional_id === nameFilter);
    }
    return rows;
  }, [tableRows, roleFilter, nameFilter, connectionsRaw, dossiersRaw]);

  const formatDate = (d: string | null) =>
    d ? new Date(d).toLocaleDateString(i18n.language) : '-';

  return (
    <div className="min-h-screen bg-gray-50 relative">
      <ZijMenuMobielKnop onClick={() => setSidebarOpen(true)} />

      {/* Cockpit Content */}
      <div className="max-w-7xl mx-auto w-full flex h-[calc(100vh-4rem)] overflow-hidden">
        <EmployerCockpitSidebar
          mobileOpen={sidebarOpen}
          onMobileClose={() => setSidebarOpen(false)}
        />

        {/* Main Content */}
        <div className="flex-1 min-w-0 overflow-y-auto bg-gray-50">
          <div className="p-3 sm:p-6">
            <EmployerCockpitHeader
              onReactieClick={() => changeView('geplaatste_opdrachten')}
              onReviewsClick={() => changeView('reviews')}
              pageTitle={viewTitles[activeView].title}
              pageSubtitle={viewTitles[activeView].subtitle}
            />
            <div className="h-5" />
            {activeView === 'reviews' ? (
              <>
                <B2bReviewsPanel />
                {individualReviewsLoading ? (
                  <div className="text-center py-12">
                    <p className="text-gray-600">{t('common:common.loading')}</p>
                  </div>
                ) : !individualReviewsRaw || individualReviewsRaw.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg">
                    <Star className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-600 mb-2">{t('cockpit.noReviews')}</p>
                    <p className="text-sm text-gray-500">
                      {t('cockpit.reviewsAppearHere')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {individualReviewsRaw.map((r: any) => (
                      <Card key={r.id} className="p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-gray-900">{r.professional_name}</span>
                              <span className="text-sm text-gray-500">
                                {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString(i18n.language, {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric'
                                }) : '-'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 mt-1">
                              {[1, 2, 3, 4, 5].map((v) => (
                                <Star
                                  key={v}
                                  className={`w-4 h-4 ${
                                    (r.average_rating ?? 0) >= v ? 'fill-amber-400 text-amber-400' : 'text-gray-200'
                                  }`}
                                />
                              ))}
                              <span className="ml-1 text-sm text-gray-600">
                                {r.average_rating?.toFixed(1) ?? '-'}
                              </span>
                            </div>
                            {r.open_comment && (
                              <p className="mt-2 text-sm text-gray-700 italic">&ldquo;{r.open_comment}&rdquo;</p>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            ) : activeView === 'gespreksverslagen' ? (
              <>
                <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">{t('cockpit.conversationReports')}</h2>
                    <span className="text-sm text-gray-500">
                      {t('cockpit.conversationReportsDesc')}
                    </span>
                  </div>
                  <div className="flex gap-2 flex-wrap items-center">
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700">{t('cockpit.professional')}:</label>
                      <select
                        value={gesprekProfessionalFilter}
                        onChange={(e) => setGesprekProfessionalFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[180px]"
                      >
                        <option value="">{t('cockpit.allProfessionals')}</option>
                        {Array.from(
                          new Map(
                            (gespreksverslagenRaw || []).map((v: any) => [
                              v.professional_id,
                              { id: v.professional_id, label: [v.professional_first_name, v.professional_last_name].filter(Boolean).join(' ') }
                            ])
                          ).values()
                        )
                          .sort((a: any, b: any) => a.label.localeCompare(b.label))
                          .map((opt: any) => (
                            <option key={opt.id} value={opt.id}>{opt.label}</option>
                          ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700">{t('cockpit.employee')}:</label>
                      <select
                        value={gesprekMedewerkerFilter}
                        onChange={(e) => setGesprekMedewerkerFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[180px]"
                      >
                        <option value="">{t('cockpit.allEmployees')}</option>
                        {(Array.from(new Set((gespreksverslagenRaw || []).map((v: any) => v.client_name).filter(Boolean))) as string[])
                          .sort((a: string, b: string) => (a || '').localeCompare(b || ''))
                          .map((name: string) => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                      </select>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={gesprekIncludeClosed}
                        onChange={(e) => setGesprekIncludeClosed(e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm font-medium text-gray-700">{t('cockpit.showClosedDossiers')}</span>
                    </label>
                  </div>
                </div>

                {gespreksverslagenLoading ? (
                  <div className="text-center py-12">
                    <p className="text-gray-600">{t('common:common.loading')}</p>
                  </div>
                ) : !gespreksverslagenRaw || gespreksverslagenRaw.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg">
                    <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-600 mb-2">{t('cockpit.noConversationReports')}</p>
                    <p className="text-sm text-gray-500">
                      {t('cockpit.conversationReportsAppearHere')} {gesprekIncludeClosed ? '' : t('cockpit.conversationReportsClosedHint')}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto bg-white rounded-lg shadow">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.arrivalDate')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.professionalRole')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.professionalName')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.employeeName')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('cockpit.sessionType')}</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.download')}</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {gespreksverslagenRaw.map((v: any) => {
                          const soortGesprek = v.session_number || (v.type === 'intake' ? t('sessionTypes.intake') : v.type === 'session' ? t('sessionTypes.session') : v.type === 'evaluation' ? t('sessionTypes.evaluation') : v.type === 'follow_up' ? t('sessionTypes.follow_up') : t('sessionTypes.other'));
                          return (
                            <tr key={v.id} className="hover:bg-gray-50">
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                {new Date(v.created_at).toLocaleDateString(i18n.language)}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                {v.professional_role || '-'}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                {[v.professional_first_name, v.professional_last_name].filter(Boolean).join(' ') || '-'}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                {v.client_name || '-'}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{soortGesprek}</td>
                              <td className="px-4 py-4 whitespace-nowrap text-right">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const base = api.defaults.baseURL || '/api';
                                    const url = `${base.replace(/\/$/, '')}/employer/dossiers/${v.dossier_id}/verslagen/${v.id}/pdf?t=${Date.now()}`;
                                    window.open(url, '_blank');
                                  }}
                                  className="flex items-center gap-1 ml-auto"
                                >
                                  <Download className="w-4 h-4" />
                                  PDF
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : activeView === 'jaarverslagen' ? (
              <>
                <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">{t('cockpit.annualReports')}</h2>
                    <span className="text-sm text-gray-500">
                      {t('cockpit.annualReportsDesc')}
                    </span>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700">{t('cockpit.year')}:</label>
                      <select
                        value={jaarFilter}
                        onChange={(e) => setJaarFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="">{t('cockpit.allYears')}</option>
                        {(Array.from(new Set((jaarverslagenRaw || []).map((r: any) => r.jaar))) as number[])
                          .sort((a: number, b: number) => b - a)
                          .map((y: number) => (
                            <option key={y} value={String(y)}>{y}</option>
                          ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700">{t('cockpit.roleProfessional')}:</label>
                      <select
                        value={reportRoleFilter}
                        onChange={(e) => setReportRoleFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[180px]"
                      >
                        <option value="">{t('roles.all')}</option>
                        {(Object.entries(PROFESSIONAL_CATEGORIES) as [ProfessionalCategoryId, typeof PROFESSIONAL_CATEGORIES[ProfessionalCategoryId]][]).map(([catId, cat]) => (
                          <optgroup key={catId} label={cat.label}>
                            {cat.types.map(type => (
                              <option key={type} value={type}>{PROFESSIONAL_TYPE_LABELS[type]}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {jaarverslagenLoading ? (
                  <div className="text-center py-12">
                    <p className="text-gray-600">{t('common:common.loading')}</p>
                  </div>
                ) : !jaarverslagenRaw || jaarverslagenRaw.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg">
                    <FileCheck className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-600 mb-2">{t('cockpit.noAnnualReports')}</p>
                    <p className="text-sm text-gray-500">
                      {t('cockpit.annualReportsAppearHere')}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto bg-white rounded-lg shadow">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.arrivalDate')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.period')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.professionalRole')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.professionalName')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.title')}</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.download')}</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {jaarverslagenRaw.map((r: any) => (
                          <tr key={r.id} className="hover:bg-gray-50">
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(r.submitted_at || r.created_at).toLocaleDateString(i18n.language)}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{r.jaar}</td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {r.professional_type_label || r.professional_type || '-'}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {[r.professional_first_name, r.professional_last_name].filter(Boolean).join(' ') || '-'}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-900 max-w-xs">
                              <span className="block truncate">{r.titel || '-'}</span>
                              {r.verificatie_url && (
                                <a href={r.verificatie_url} target="_blank" rel="noreferrer" className="text-xs text-[#0B1F3A] underline">
                                  {t('cockpit.jaarverslagVerify', 'Echtheid controleren')}
                                </a>
                              )}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-right">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const url = `/api/employer/reports/${r.id}/pdf?t=${Date.now()}`;
                                  window.open(url, '_blank');
                                }}
                                className="flex items-center gap-1 ml-auto"
                              >
                                <Download className="w-4 h-4" />
                                PDF
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : activeView === 'geplaatste_opdrachten' ? (
              <>
                <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                  <div className="flex gap-2 flex-wrap">
                    <Link to="/opdrachten/nieuw">
                      <Button variant="outline">
                        <Briefcase className="w-4 h-4 mr-2" />
                        {t('cockpit.newAssignment')}
                      </Button>
                    </Link>
                    <Link to="/employer/dashboard/zoeken/uitgebreid">
                      <Button>
                        <Search className="w-4 h-4 mr-2" />
                        {t('cockpit.searchProfessional')}
                      </Button>
                    </Link>
                    <Link to="/employer/dashboard/zoeken?route=vacatures">
                      <Button variant="outline" size="sm">
                        {t('cockpit.manageAllAssignments')}
                      </Button>
                    </Link>
                  </div>
                </div>

                {geplaatsteOpdrachtenLoading ? (
                  <div className="text-center py-12">
                    <p className="text-gray-600">{t('common:common.loading')}</p>
                  </div>
                ) : !geplaatsteOpdrachtenRaw || geplaatsteOpdrachtenRaw.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg">
                    <Briefcase className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-600 mb-2">{t('cockpit.noPlacedAssignments')}</p>
                    <p className="text-sm text-gray-500 mb-4">
                      {t('cockpit.placeAssignmentHint')}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2 justify-center">
                      <Link to="/employer/dashboard/zoeken/uitgebreid">
                        <Button>
                          <Search className="w-4 h-4 mr-2" />
                          {t('cockpit.searchAndPlace')}
                        </Button>
                      </Link>
                      <Link to="/opdrachten/nieuw">
                        <Button variant="outline">
                          <Briefcase className="w-4 h-4 mr-2" />
                          {t('cockpit.placeOnPlatform')}
                        </Button>
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {geplaatsteOpdrachtenRaw.map((item: any) => (
                      <Card key={item.id} className="overflow-hidden">
                        <div className="p-6">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-2">
                                <h3 className="text-lg font-semibold text-gray-900">{item.titel}</h3>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  item.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                                  item.status === 'published' ? 'bg-green-100 text-green-800' :
                                  item.status === 'closed' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-blue-100 text-blue-800'
                                }`}>
                                  {item.status === 'draft' ? t('vacancyStatus.draft') : item.status === 'published' ? t('vacancyStatus.published') : item.status === 'closed' ? t('vacancyStatus.closed') : t('vacancyStatus.filled')}
                                </span>
                                {item.is_direct_to_professional && (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                                    {t('cockpit.directToProfessional')}
                                  </span>
                                )}
                                {item.is_platform_vacancy && (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700">
                                    {t('cockpit.platform')}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 line-clamp-2 mb-2">{item.beschrijving}</p>
                              <p className="text-xs text-gray-500">
                                {t('cockpit.postedOn', { date: new Date(item.created_at).toLocaleDateString(i18n.language) })}
                              </p>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <Link to={`/employer/dashboard/zoeken/vacatures/${item.id}/bewerken`}>
                                <Button variant="outline" size="sm">{t('cockpit.edit')}</Button>
                              </Link>
                              <Link to={`/opdrachten/${item.id}`}>
                                <Button size="sm">{t('cockpit.details')}</Button>
                              </Link>
                            </div>
                          </div>

                          {/* Professionals / Reacties: applications + uitgenodigde professional */}
                          {(() => {
                            const applications = item.applications || [];
                            const targetProf = item.target_professional;
                            const targetStatus = item.target_professional_status;
                            const hasInvited = targetProf && targetStatus === 'uitgenodigd';
                            const allEntries = [
                              ...(hasInvited ? [{
                                id: `target-${targetProf.id}`,
                                professional_first_name: targetProf.first_name ?? '',
                                professional_last_name: targetProf.last_name ?? '',
                                professional_email: targetProf.email ?? '',
                                application_status: 'uitgenodigd'
                              }] : []),
                              ...applications
                            ];
                            if (allEntries.length === 0) {
                              return (
                                <div className="mt-4 pt-4 border-t border-gray-100">
                                  <p className="text-sm text-gray-500 italic">
                                    {t('cockpit.noReactions')}
                                  </p>
                                </div>
                              );
                            }
                            return (
                              <div className="mt-4 pt-4 border-t border-gray-100">
                                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                  <UserPlus className="w-4 h-4" />
                                  {t('cockpit.professionalsReactions', { count: allEntries.length })}
                                </h4>
                                <div className="space-y-3">
                                  {allEntries.map((entry: any) => {
                                    const status = entry.application_status || 'nieuw';
                                    const statusLabel = getApplicationStatusLabel(status);
                                    return (
                                      <div
                                        key={entry.id}
                                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                                      >
                                        <div className="flex items-center gap-3">
                                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                            <span className="text-sm font-medium text-blue-700">
                                              {((entry.professional_first_name ?? '')[0] || (entry.professional_last_name ?? '')[0] || '?').toUpperCase()}
                                            </span>
                                          </div>
                                          <div>
                                            <p className="font-medium text-gray-900">
                                              {[entry.professional_first_name, entry.professional_last_name].filter(Boolean).join(' ') || t('cockpit.unknown')}
                                            </p>
                                            <p className="text-xs text-gray-500">{entry.professional_email ?? ''}</p>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                            status === 'uitgenodigd' ? 'bg-amber-100 text-amber-800' :
                                            status === 'nieuw' ? 'bg-blue-100 text-blue-800' :
                                            status === 'geaccepteerd' ? 'bg-green-100 text-green-800' :
                                            status === 'onderhandelingen' ? 'bg-yellow-100 text-yellow-800' :
                                            status === 'afgerond' ? 'bg-gray-100 text-gray-800' :
                                            status === 'afgewezen' ? 'bg-red-100 text-red-800' :
                                            'bg-gray-100 text-gray-800'
                                          }`}>
                                            {statusLabel}
                                          </span>
                                          <Link to={`/opdrachten/${item.id}`}>
                                            <Button variant="outline" size="sm">{t('cockpit.view')}</Button>
                                          </Link>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            ) : activeView === 'trajecten' ? (
              <>
                <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700">{t('cockpit.filterByRole')}</label>
                      <select
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="">{t('roles.all')}</option>
                        {(Object.entries(PROFESSIONAL_CATEGORIES) as [ProfessionalCategoryId, typeof PROFESSIONAL_CATEGORIES[ProfessionalCategoryId]][]).map(([catId, cat]) => (
                          <optgroup key={catId} label={cat.label}>
                            {cat.types.map(type => (
                              <option key={type} value={type}>{PROFESSIONAL_TYPE_LABELS[type]}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700">{t('cockpit.filterByName')}</label>
                      <select
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[180px]"
                      >
                        <option value="">{t('cockpit.allProfessionals')}</option>
                        {professionalNameOptions.map((opt) => (
                          <option key={opt.id} value={opt.id}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => changeView('afgesloten_dossiers')}>
                    <Archive className="w-4 h-4 mr-2" />
                    {t('cockpit.closedDossiers')}
                  </Button>
                </div>

                {dossiersLoading ? (
                  <div className="text-center py-12">
                    <p className="text-gray-600">{t('common:common.loading')}</p>
                  </div>
                ) : filteredTableRows.length > 0 ? (
                  <div className="overflow-x-auto bg-white rounded-lg shadow">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.registratienummer')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.professionalRole')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.professionalName')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.newEmployee')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.trajectStatus')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.acceptanceDate')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.closingDate')}</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredTableRows.map((row: any) => (
                          <tr
                            key={row.id}
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => {
                              if (row.dossier_id) {
                                navigate(`/employer/dossiers/${row.dossier_id}/verslagen`);
                              }
                            }}
                          >
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-mono font-medium text-gray-900">
                              {row.registratienummer}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {row.professional_role}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div className="flex flex-col" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center gap-2">
                                  <span>{row.professional_name || '-'}</span>
                                  {row.review_count > 0 && !row.reviews_visible && (
                                    <span
                                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium"
                                      title={t('cockpit.reviewsThresholdHint', 'Reviews worden pas zichtbaar vanaf 3 ingestuurde evaluaties (AVG)')}
                                    >
                                      <Star className="w-3.5 h-3.5 text-gray-400" />
                                      {t('cockpit.reviewsThreshold', 'Nog {{count}} evaluatie(s) nodig', { count: row.reviews_needed })}
                                    </span>
                                  )}
                                  {row.review_count > 0 && row.reviews_visible && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setReviewModalProfessional({
                                          professional_id: row.professional_id,
                                          professional_name: row.professional_name || '-',
                                          average_rating: row.average_rating ?? 0,
                                          review_count: row.review_count,
                                          questions: (row.review_questions || []).map((q: any) => ({
                                            label: q.label,
                                            question: q.question,
                                            avg: q.avg ?? 0
                                          }))
                                        });
                                      }}
                                      className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 hover:bg-amber-100 text-xs font-medium"
                                    >
                                      <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
                                      {row.average_rating?.toFixed(1) ?? '-'} ({row.review_count})
                                    </button>
                                  )}
                                  {row.isAfgewezen && activeConnections.filter((c: any) => c.professional_id !== row.professional_id).length > 0 && (
                                    <div className="relative">
                                      <button
                                        type="button"
                                        className="p-0.5 rounded hover:bg-gray-100"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOpenDropdownId(openDropdownId === row.id ? null : row.id);
                                        }}
                                      >
                                        <ChevronDown className="w-4 h-4 text-gray-500" />
                                      </button>
                                      {openDropdownId === row.id && (
                                        <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[180px] py-1">
                                          <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100">
                                            {t('cockpit.reassignTo')}
                                          </div>
                                          {activeConnections
                                            .filter((c: any) => c.professional_id !== row.professional_id)
                                            .map((c: any) => (
                                              <button
                                                key={c.professional_id}
                                                type="button"
                                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setReassignDossier(row.raw);
                                                  setReassignPreselectedProfessionalId(c.professional_id);
                                                  setOpenDropdownId(null);
                                                }}
                                              >
                                                {c.first_name} {c.last_name}
                                              </button>
                                            ))}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                {row.isAfgewezen && row.acceptance_notes && (
                                  <span className="text-xs text-red-600 mt-1 block" title={row.acceptance_notes}>
                                    {row.acceptance_notes.length > 50 ? `${row.acceptance_notes.slice(0, 50)}...` : row.acceptance_notes}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span
                                className={`px-2 py-1 text-xs rounded-full ${
                                  row.nieuwe_medewerker === t('status.pending_acceptance')
                                    ? 'bg-amber-100 text-amber-800'
                                    : row.nieuwe_medewerker === t('status.afgewezen')
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-green-100 text-green-800'
                                }`}
                              >
                                {row.nieuwe_medewerker}
                              </span>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {row.status_traject}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                              {row.professional_accepted_at ? formatDate(row.professional_accepted_at) : '-'}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                              {row.end_date ? formatDate(row.end_date) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 bg-white rounded-lg">
                    <FolderOpen className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-600">{t('cockpit.noTrajects')}</p>
                    <Link
                      to="/employer/dashboard/zoeken/uitgebreid"
                      className="inline-flex items-center mt-4 text-green-600 hover:text-green-700 font-medium"
                    >
                      <Search className="w-4 h-4 mr-2" />
                      {t('cockpit.searchProfessionals')}
                    </Link>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                  <h2 className="text-xl font-semibold text-gray-900">{t('cockpit.closedDossiers')}</h2>
                  <Button variant="outline" onClick={() => changeView('trajecten')}>
                    <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
                    {t('cockpit.backToTrajects')}
                  </Button>
                </div>

                {closedDossiersLoading ? (
                  <div className="text-center py-12">
                    <p className="text-gray-600">{t('common:common.loading')}</p>
                  </div>
                ) : closedDossiers.length > 0 ? (
                  <div className="overflow-x-auto bg-white rounded-lg shadow">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.registratienummer')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('cockpit.professional')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.status')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.closingDate')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.actions')}</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {closedDossiers.map((d: any) => (
                          <tr key={d.id} className="hover:bg-gray-50">
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-mono font-medium text-gray-900">
                              {d.dossier_code}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {d.professional_first_name} {d.professional_last_name}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                                {getStatusTrajectLabel(d.status)}
                              </span>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                              {d.end_date ? formatDate(d.end_date) : '-'}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => reactivateDossierMutation.mutate(d.id)}
                                className="text-green-600 hover:text-green-700"
                                disabled={reactivateDossierMutation.isPending}
                              >
                                <RotateCcw className="w-4 h-4 mr-1" />
                                {t('cockpit.reopen')}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 bg-white rounded-lg">
                    <Archive className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-600">{t('cockpit.noClosedDossiers')}</p>
                    <Button variant="outline" onClick={() => changeView('trajecten')} className="mt-4">
                      {t('cockpit.backToTrajects')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {reviewModalProfessional && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('cockpit.ratingsTitle', { name: reviewModalProfessional.professional_name })}
                </h3>
                <button
                  onClick={() => setReviewModalProfessional(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex items-center gap-2 mb-6">
                <div className="flex items-center">
                  <Star className="w-6 h-6 fill-amber-500 text-amber-500" />
                  <span className="ml-1 text-xl font-semibold text-gray-900">
                    {reviewModalProfessional.average_rating.toFixed(1)}
                  </span>
                </div>
                <span className="text-sm text-gray-500">
                  {reviewModalProfessional.review_count !== 1
                    ? t('cockpit.averageBasedOnPlural', { count: reviewModalProfessional.review_count })
                    : t('cockpit.averageBasedOn', { count: reviewModalProfessional.review_count })}
                </span>
              </div>
              {reviewModalProfessional.questions.length > 0 && (
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-700">{t('cockpit.breakdownByQuestion')}</h4>
                  {reviewModalProfessional.questions.map((q: any, i: number) => (
                    <div key={i} className="border-b border-gray-100 pb-3 last:border-0">
                      <p className="text-sm font-medium text-gray-800">{q.label}</p>
                      <p className="text-xs text-gray-600 mb-1">{q.question}</p>
                      <div className="flex items-center gap-2">
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((v) => (
                            <Star
                              key={v}
                              className={`w-4 h-4 ${
                                q.avg >= v ? 'fill-amber-400 text-amber-400' : 'text-gray-200'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-sm text-gray-600">
                          {q.avg > 0 ? q.avg.toFixed(1) : '-'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {reassignDossier && (
        <ReassignModal
          key={`${reassignDossier.id}-${reassignPreselectedProfessionalId || ''}`}
          dossier={reassignDossier}
          professionals={activeConnections.filter((c: any) => c.professional_id !== reassignDossier.professional_id)}
          preselectedProfessionalId={reassignPreselectedProfessionalId}
          onSubmit={(data) => {
            createDossierMutation.mutate(data);
            setReassignPreselectedProfessionalId(null);
          }}
          onCancel={() => {
            setReassignDossier(null);
            setReassignPreselectedProfessionalId(null);
          }}
          isSubmitting={createDossierMutation.isPending}
        />
      )}
    </div>
  );
};
